import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { CreditCard, Nfc, ShieldCheck, Smartphone } from 'lucide-react'
import { AccessDenied } from '@/components/permission-guard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  isForbidden,
  queryKeys,
  useApprovePosGrant,
  useApprovePosGrantByCode,
  useEvent,
  useEventPosGrants,
  useEventTerminals,
  useRevokePosGrant,
  useSetPosBoothTrusted,
  useUpdateEvent,
  useUpdateVendorEvent,
  useVendorEvents,
} from '@/lib/api/queries'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import type { Event, EventTerminalDevice, PosGrant, PosGrantApproval, TrustedPosBooth, VendorEvent } from '@/lib/api/types'
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { Banner, Spinner } from '@/features/fraud/shared'
import {
  CARD_CURRENCIES, cardsChargeCurrency, cardsComingSoonText, eventCardFeeText, eventTakesCards, feeLabel,
} from '@/features/card-payments/labels'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, relativeTime } from '@/features/fraud/format'

export const Route = createFileRoute('/_authenticated/events/$eventId/card-payments')({
  component: CardPaymentsPage,
})

type Notice = { kind: 'success' | 'error' | 'info'; text: string }

const APPROVAL_LABEL: Record<PosGrantApproval, string> = {
  staff: 'Staff phone',
  personal: 'Personal login',
  trusted_booth: 'Trusted booth',
  not_required: 'No approval needed',
  code: 'Staff, by code',
  list: 'Staff, from the waiting list',
  '': 'Approved',
}

const MODEL_LABEL: Record<string, string> = {
  PAX_D135: 'PAX D135',
  IOS_TAP_TO_PAY: 'Tap to Pay on iPhone',
}

const STATUS_CHIP: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  activation_pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
}
const NEUTRAL_CHIP = 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'

const SECTION = 'rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto'

/** The grant a Tap to Pay device's phone holds here: its live one, else its latest. */
function grantForPhone(grants: PosGrant[], phone: number | null | undefined): PosGrant | undefined {
  if (!phone) return undefined
  const own = grants.filter((g) => g.phone === phone)
  return own.find((g) => g.status === 'active') ?? own[0]
}

function approvedNotice(row: PosGrant): Notice {
  if (row.tap_to_pay_error) {
    return { kind: 'info', text: `${row.name} can take cards at this event, but Tap to Pay is not ready on that iPhone: ${row.tap_to_pay_error}` }
  }
  return { kind: 'success', text: `${row.name} can now take cards at this event.` }
}

/**
 * Card payments: staff with can_add_terminal at this event turn cards on, for
 * the event or per location, and approve the phones that take them (Tap to
 * Pay, D135) through the POS. A booth's shared login asks from its phone and
 * waits here; staff approve it from the list or by the code the phone shows.
 * Trusting a booth approves every later phone signed in as that booth for the
 * event. The phone sections show once cards are on somewhere, or while a
 * phone is still approved or waiting (so it can be revoked after cards go off).
 */
function CardPaymentsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const canManage = usePermission('can_manage_card_phones')
  const { data: event } = useEvent(eventIdNum)
  const phones = useEventPosGrants(eventIdNum, { enabled: canManage, live: true })
  const readers = useEventTerminals(eventIdNum)
  const devices = readers.data ?? []
  const [notice, setNotice] = useState<Notice | null>(null)
  // How many locations take cards, reported by CardPaymentsSetting, which
  // loads the locations (an override can turn a location on with the event off).
  const [locationCounts, setLocationCounts] = useState<LocationCounts | null>(null)
  const loaded = !!phones.data

  const grants = phones.data?.grants ?? []
  const trusted = phones.data?.trusted_booths ?? []
  const approvalRequired = event?.pos_phone_approval_required !== false
  const takesCards = !!event && eventTakesCards(event)
  const cardsOn = !!event && takesCards && cardsChargeCurrency(event.currency)
    && (event.card_payments_enabled === true || (locationCounts?.on ?? 0) > 0)
  const waitingCount = grants.filter((g) => g.status === 'pending').length
  const approvedCount = grants.filter((g) => g.status === 'active').length
  const showPhones = cardsOn || waitingCount > 0 || approvedCount > 0
  // A phone that asked before approval was turned off can still be approved.
  const showWaiting = approvalRequired || waitingCount > 0
  const showReaders = showPhones || devices.length > 0

  const groups: NavGroup[] = [
    {
      label: 'Card payments', icon: <CreditCard className="h-4 w-4 mr-2" aria-hidden />,
      sections: [
        { id: 'cp-switch', label: 'Event switch' },
        ...(takesCards ? [{ id: 'cp-locations', label: 'Locations', count: locationCounts ? `${locationCounts.on}/${locationCounts.total}` : undefined }] : []),
      ],
    },
    ...(showPhones ? [{
      label: 'Phones', icon: <Smartphone className="h-4 w-4 mr-2" aria-hidden />,
      sections: [
        ...(showWaiting ? [{ id: 'cp-waiting', label: 'Waiting', count: waitingCount > 0 ? String(waitingCount) : undefined, alert: waitingCount > 0 }] : []),
        { id: 'cp-approved', label: 'Approved', count: String(approvedCount) },
        ...(approvalRequired ? [{ id: 'cp-trusted', label: 'Trusted booths', count: trusted.length > 0 ? String(trusted.length) : undefined }] : []),
        { id: 'cp-approval', label: 'Approval rule' },
      ],
    }] : []),
    ...(showReaders ? [{
      label: 'Readers', icon: <Nfc className="h-4 w-4 mr-2" aria-hidden />,
      sections: [{ id: 'cp-readers', label: 'Card readers', count: String(devices.length) }],
    }] : []),
  ]
  const [current, goTo] = useCurrentSection(loaded ? groups.flatMap((g) => g.sections.map((s) => s.id)).join(',') : '')

  // Permission guard - now safe after all hooks
  if (!canManage || isForbidden(phones.error)) {
    return <AccessDenied message="Only event staff who manage card readers can set up card payments at this event." />
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">Card payments</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          {event?.name ? `${event.name} · ` : ''}Turn card payments on and control which phones take them.
        </p>
      </div>

      {phones.isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : !phones.data ? (
        <div className="space-y-3">
          <Banner kind="error">{apiErrorDetail(phones.error, 'Could not load the phones.')}</Banner>
          <Button variant="outline" size="sm" onClick={() => void phones.refetch()}>Try again</Button>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <SectionNav groups={groups} current={current} onPick={goTo} />
          <div className="flex-1 min-w-0 space-y-6">
            {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}
            {event && <CardPaymentsSetting event={event} onNotice={setNotice} onLocations={setLocationCounts} />}
            {showPhones ? (
              <section className="space-y-4" aria-labelledby="phones-heading">
                <h2 id="phones-heading" className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-white">
                  <Smartphone className="h-5 w-5 text-neutral-400" aria-hidden /> Phones
                </h2>
                {showWaiting && <div id="cp-waiting" className="scroll-mt-24"><WaitingPhones eventId={eventIdNum} grants={grants} onNotice={setNotice} /></div>}
                <div id="cp-approved" className="scroll-mt-24"><ApprovedPhones eventId={eventIdNum} grants={grants} devices={devices} onNotice={setNotice} /></div>
                {approvalRequired && <div id="cp-trusted" className="scroll-mt-24"><TrustedBooths eventId={eventIdNum} trusted={trusted} onNotice={setNotice} /></div>}
                <div id="cp-approval" className="scroll-mt-24"><ApprovalSetting eventId={eventIdNum} required={event?.pos_phone_approval_required} onNotice={setNotice} /></div>
              </section>
            ) : takesCards && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Turn card payments on to approve phones.</p>
            )}
            {showReaders && <div id="cp-readers" className="scroll-mt-24"><CardReaders grants={grants} devices={devices} error={readers.error} /></div>}
          </div>
        </div>
      )}
    </div>
  )
}

type LocationCounts = { on: number; total: number }

type NavGroup = {
  label: string
  icon: ReactNode
  sections: { id: string; label: string; count?: string; alert?: boolean }[]
}

/**
 * The section on screen, for the nav: the topmost one in the band below the
 * header, as Settings does. `ids` (comma-joined) re-observes when sections
 * come and go.
 */
function useCurrentSection(ids: string) {
  const [current, setCurrent] = useState<string | null>(null)
  // A section picked in the nav stays current while the page scrolls to it:
  // the last sections can't reach the top, so the band would name another.
  const picked = useRef<{ id: string; until: number } | null>(null)
  useEffect(() => {
    if (!ids || typeof IntersectionObserver === 'undefined') return
    const sections = ids.split(',').map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el)
    if (sections.length === 0) return
    const visible = new Set<string>()
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id)
        else visible.delete(entry.target.id)
      }
      if (picked.current && Date.now() < picked.current.until) return
      picked.current = null
      const shown = sections.filter((el) => visible.has(el.id))
      // At the bottom of the page the last section on screen is the one read.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
      const pick = atBottom ? shown[shown.length - 1] : shown[0]
      if (pick) setCurrent(pick.id)
    }, { rootMargin: '-96px 0px -55% 0px' })
    sections.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [ids])
  const go = (id: string) => {
    picked.current = { id, until: Date.now() + 1000 }
    setCurrent(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  return [current, go] as const
}

/** The page's contents, sticky beside it; a dropdown on a phone (as Settings). */
function SectionNav({ groups, current, onPick }: {
  groups: NavGroup[]
  current: string | null
  onPick: (id: string) => void
}) {
  return (
    <aside className="md:w-56 shrink-0">
      <select
        aria-label="Card payments section"
        className="md:hidden w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={current ?? ''}
        onChange={(e) => onPick(e.target.value)}
      >
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.sections.map((section) => (
              <option key={section.id} value={section.id}>{section.label}{section.count ? ` (${section.count})` : ''}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <nav className="hidden md:block sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto space-y-4 pb-4">
        {groups.map((group) => (
          <div key={group.label}>
            <button
              type="button"
              onClick={() => group.sections[0] && onPick(group.sections[0].id)}
              className="flex items-center w-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded-md text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              {group.icon}
              {group.label}
            </button>
            <ul className="mt-0.5 space-y-0.5">
              {group.sections.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => onPick(section.id)}
                    className={cn(
                      'flex items-center justify-between gap-2 w-full text-left pl-9 pr-3 py-1.5 text-sm rounded-md border-l-2 transition-colors',
                      current === section.id
                        ? 'border-blue-600 bg-neutral-100 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-medium'
                        : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
                    )}
                  >
                    <span>{section.label}</span>
                    {section.count && (
                      <span className={cn(
                        'text-xs tabular-nums',
                        section.alert ? 'min-w-5 px-1.5 rounded-full bg-blue-600 text-white text-center font-semibold' : 'text-neutral-400 dark:text-neutral-500',
                      )}>
                        {section.count}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  )
}

const SELECT = 'px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed'
type CardChoice = 'inherit' | 'on' | 'off'

function choiceOf(value: boolean | null | undefined): CardChoice {
  return value == null ? 'inherit' : value ? 'on' : 'off'
}

function locationLabel(ve: VendorEvent) {
  const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ve.name
  return `${vendorName}${ve.booth ? ` · Booth ${ve.booth}` : ''}`
}

/**
 * The event's card payments switch, and per location: a location takes the
 * event's setting unless it has its own, so cards can be on at the bar only,
 * and can carry its own card fee. The backend refuses a sale where they are
 * off, and a currency the readers do not charge.
 */
function CardPaymentsSetting({ event, onNotice, onLocations }: {
  event: Event
  onNotice: (notice: Notice | null) => void
  /** How many locations take cards, of how many (null until they load). */
  onLocations: (counts: LocationCounts | null) => void
}) {
  // The location PATCH also needs vendor access; the backend asks
  // can_add_terminal too.
  const canEditVendors = usePermission('can_edit_vendors')
  // The event PATCH also needs event settings access (change_event); the
  // backend asks can_add_terminal for the switch itself.
  const canEditEvents = usePermission('can_edit_events')
  const updateEvent = useUpdateEvent()
  const updateLocation = useUpdateVendorEvent()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const takesCards = eventTakesCards(event)
  const locations = useVendorEvents(event.id, { aggregates: false, limit: 1000 }, { enabled: takesCards })
  const eventOn = event.card_payments_enabled === true
  const currency = (event.currency || '').toUpperCase()
  const currencyCharged = CARD_CURRENCIES.includes(currency)

  const rows = [...(locations.data?.results ?? [])].sort((a, b) => locationLabel(a).localeCompare(locationLabel(b)))
  const needle = search.trim().toLowerCase()
  const shown = needle ? rows.filter((ve) => locationLabel(ve).toLowerCase().includes(needle)) : rows
  const onCount = rows.filter((ve) => ve.card_payments ?? eventOn).length
  const loaded = !!locations.data
  useEffect(() => {
    onLocations(loaded ? { on: onCount, total: rows.length } : null)
  }, [loaded, onCount, rows.length, onLocations])

  const setEventCards = async (next: boolean) => {
    if (!next && !window.confirm(
      'Turn card payments off? Locations set to Same as event stop taking cards. A location set to On keeps taking them.',
    )) return
    onNotice(null)
    setSaving('event')
    try {
      await updateEvent.mutateAsync({ eventId: event.id, data: { card_payments_enabled: next } })
      // Show the saved value, not the old one until a background refetch lands.
      await queryClient.refetchQueries({ queryKey: queryKeys.event(event.id) })
      onNotice({ kind: 'success', text: next ? 'Card payments are on for this event.' : 'Card payments are off for this event.' })
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not change card payments for this event.') })
    } finally {
      setSaving(null)
    }
  }

  const eventFee = feeLabel(event.variable_fee_percentage, event.fixed_fee_amount, currency)
  const [editingFee, setEditingFee] = useState<{ id: number; pct: string; fixed: string } | null>(null)

  const saveFee = async (ve: VendorEvent, fee: { pct: string; fixed: string } | null) => {
    // Typed as a whole percent, stored as a fraction (2.9 → 0.029).
    let data: { card_fee_percentage: number | null; card_fee_fixed: number | null }
    if (fee === null) {
      data = { card_fee_percentage: null, card_fee_fixed: null }
    } else {
      const pct = fee.pct.trim() === '' ? 0 : Number(fee.pct)
      const fixed = fee.fixed.trim() === '' ? 0 : Number(fee.fixed)
      if (!Number.isFinite(pct) || pct < 0 || pct >= 100 || !Number.isFinite(fixed) || fixed < 0) {
        onNotice({ kind: 'error', text: 'Enter a percent from 0 to under 100 and an amount of 0 or more.' })
        return
      }
      data = { card_fee_percentage: Math.round(pct * 100) / 10000, card_fee_fixed: Math.round(fixed * 100) / 100 }
    }
    onNotice(null)
    setSaving(`fee-${ve.id}`)
    try {
      await updateLocation.mutateAsync({ vendorEventId: ve.id, eventId: event.id, data })
      await queryClient.refetchQueries({ queryKey: queryKeys.vendorEvents(event.id) })
      setEditingFee(null)
      onNotice({
        kind: 'success',
        text: fee === null
          ? `${locationLabel(ve)}: the card fee is the event's again.`
          : `${locationLabel(ve)}: card fee ${feeLabel(data.card_fee_percentage, data.card_fee_fixed, currency)}.`,
      })
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not change the card fee for that location.') })
    } finally {
      setSaving(null)
    }
  }

  const setLocation = async (ve: VendorEvent, choice: CardChoice) => {
    onNotice(null)
    setSaving(`ve-${ve.id}`)
    try {
      await updateLocation.mutateAsync({
        vendorEventId: ve.id,
        eventId: event.id,
        data: { card_payments: choice === 'inherit' ? null : choice === 'on' },
      })
      await queryClient.refetchQueries({ queryKey: queryKeys.vendorEvents(event.id) })
      onNotice({ kind: 'success', text: `${locationLabel(ve)}: card payments ${choice === 'inherit' ? 'follow the event' : choice}.` })
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not change card payments for that location.') })
    } finally {
      setSaving(null)
    }
  }

  return (
    <>
    <Card id="cp-switch" className="scroll-mt-24">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4" /> Card payments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!takesCards ? (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">This event can’t take cards: it has no Finix merchant.</p>
        ) : (
          <>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="card_payments_enabled"
                checked={eventOn}
                // A currency the readers don't charge yet: shown, but can't be turned on.
                disabled={!canEditEvents || saving !== null || (!currencyCharged && !eventOn)}
                onChange={(e) => void setEventCards(e.target.checked)}
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded disabled:opacity-60"
              />
              <label htmlFor="card_payments_enabled" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                <span className="font-medium">Take card payments at this event</span>
                {!currencyCharged && (
                  <span className="ml-2 px-2 py-0.5 inline-flex align-middle text-xs leading-5 font-semibold rounded-full whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    Coming soon
                  </span>
                )}
                <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                  {currencyCharged ? (
                    <>
                      Card readers and Tap to Pay on iPhone, charged in {CARD_CURRENCIES.join(', ')}.
                      {' '}Card fee on a tap: {eventCardFeeText(event)}. A location can set its own below.
                    </>
                  ) : (
                    <>
                      {cardsComingSoonText(event.currency)}. Card readers and Tap to Pay on iPhone charge {CARD_CURRENCIES.join(', ')} only for now,
                      {' '}and this event charges {currency || 'another currency'}.
                    </>
                  )}
                  {!canEditEvents && ' Changing this also needs access to the event settings.'}
                </span>
              </label>
            </div>

            {!currencyCharged && (eventOn || onCount > 0) && (
              <Banner kind="info">
                Card readers and Tap to Pay charge {CARD_CURRENCIES.join(', ')} only. This event charges {currency || 'another currency'}, so no cards are taken.
              </Banner>
            )}
          </>
        )}
      </CardContent>
    </Card>

    {takesCards && (
      <Card id="cp-locations" className="scroll-mt-24">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg">Locations</CardTitle>
            {rows.length > 10 && (
              <Input className="max-w-xs" placeholder="Search locations" value={search} onChange={(e) => setSearch(e.target.value)} />
            )}
          </div>
          <CardDescription>Each location takes the event’s setting unless it has its own, and can carry its own card fee.</CardDescription>
        </CardHeader>
        <CardContent>
            <div className="space-y-2">
              {locations.isLoading ? (
                <div className="py-4"><Spinner /></div>
              ) : locations.error ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {isForbidden(locations.error) ? 'Seeing the locations needs vendor access at this event.' : 'Could not load the locations.'}
                </p>
              ) : rows.length === 0 ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">This event has no locations yet.</p>
              ) : (
                <>
                  <ul className="max-h-96 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800 rounded-md border border-neutral-200 dark:border-neutral-800">
                    {shown.map((ve) => {
                      const ownFee = ve.card_fee_percentage != null || ve.card_fee_fixed != null
                      const editing = editingFee?.id === ve.id ? editingFee : null
                      return (
                        <li key={ve.id} className="px-3 py-2 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <span className="text-sm text-neutral-800 dark:text-neutral-200 truncate min-w-0 flex-1">{locationLabel(ve)}</span>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">
                              Card fee: {ownFee ? feeLabel(ve.card_fee_percentage, ve.card_fee_fixed, currency) : `Event (${eventFee})`}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!canEditVendors || saving !== null}
                              onClick={() => setEditingFee(editing ? null : {
                                id: ve.id,
                                pct: ownFee ? String(Math.round((ve.card_fee_percentage ?? 0) * 10000) / 100) : '',
                                fixed: ownFee ? String(ve.card_fee_fixed ?? 0) : '',
                              })}
                            >
                              Edit
                            </Button>
                            <select
                              aria-label={`Card payments at ${locationLabel(ve)}`}
                              className={SELECT}
                              value={choiceOf(ve.card_payments)}
                              disabled={!canEditVendors || saving !== null}
                              onChange={(e) => void setLocation(ve, e.target.value as CardChoice)}
                            >
                              <option value="inherit">Same as event ({eventOn ? 'On' : 'Off'})</option>
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                          </div>
                          {editing && (
                            <div className="flex flex-wrap items-center gap-2 pl-3 text-sm text-neutral-700 dark:text-neutral-300">
                              <span>Card fee</span>
                              <Input
                                className="w-20" inputMode="decimal" aria-label="Card fee percent" placeholder="0"
                                value={editing.pct} onChange={(e) => setEditingFee({ ...editing, pct: e.target.value })}
                              />
                              <span>% +</span>
                              <Input
                                className="w-24" inputMode="decimal" aria-label="Card fee amount" placeholder="0.00"
                                value={editing.fixed} onChange={(e) => setEditingFee({ ...editing, fixed: e.target.value })}
                              />
                              <span>{currency}</span>
                              <Button size="sm" disabled={saving !== null} onClick={() => void saveFee(ve, editing)}>Save</Button>
                              <Button size="sm" variant="outline" disabled={saving !== null || !ownFee} onClick={() => void saveFee(ve, null)}>
                                Use the event’s
                              </Button>
                              <Button size="sm" variant="ghost" disabled={saving !== null} onClick={() => setEditingFee(null)}>Cancel</Button>
                            </div>
                          )}
                        </li>
                      )
                    })}
                    {shown.length === 0 && <li className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">No location matches.</li>}
                  </ul>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {onCount} of {rows.length} location{rows.length === 1 ? '' : 's'} take{onCount === 1 ? 's' : ''} cards.
                    {!canEditVendors && ' Changing a location also needs vendor access at this event.'}
                  </p>
                </>
              )}
            </div>
        </CardContent>
      </Card>
    )}
    </>
  )
}

function ApprovalSetting({ eventId, required, onNotice }: {
  eventId: number
  required: boolean | undefined
  onNotice: (notice: Notice | null) => void
}) {
  // The event PATCH also needs event settings access (change_event).
  const canEditEvents = usePermission('can_edit_events')
  const update = useUpdateEvent()
  const queryClient = useQueryClient()
  // Until the saved value is shown: the mutation settles before the refetch.
  const [saving, setSaving] = useState(false)

  const change = async (next: boolean) => {
    if (!next && !window.confirm(
      'Turn approval off? Any login that may sell at a booth will take cards on any phone at once, shared booth logins included.',
    )) return
    onNotice(null)
    setSaving(true)
    try {
      await update.mutateAsync({ eventId, data: { pos_phone_approval_required: next } })
      // Show the saved value, not the old one until a background refetch lands.
      await queryClient.refetchQueries({ queryKey: queryKeys.event(eventId) })
      onNotice({
        kind: 'success',
        text: next ? 'Shared booth logins now need approval before taking cards.' : 'Booth logins take cards without approval.',
      })
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not change the setting.') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="pos_phone_approval_required"
            checked={required !== false}
            disabled={required === undefined || !canEditEvents || saving}
            onChange={(e) => void change(e.target.checked)}
            className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
          />
          <label htmlFor="pos_phone_approval_required" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
            <span className="font-medium">Booth logins need approval before taking cards</span>
            <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
              A shared booth login (the one on the login sheet) asks for approval on each new phone. Staff phones and personal logins are
              approved at once either way.
              {!canEditEvents && ' Changing this also needs access to the event settings.'}
            </span>
          </label>
        </div>
      </CardContent>
    </Card>
  )
}

function WaitingPhones({ eventId, grants, onNotice }: {
  eventId: number
  grants: PosGrant[]
  onNotice: (notice: Notice | null) => void
}) {
  const approve = useApprovePosGrant(eventId)
  // One approval at a time, even on a fast double click (the disabled
  // state lands a render later).
  const approving = useRef(false)
  const waiting = grants.filter((g) => g.status === 'pending')

  const run = async (row: PosGrant, trustBooth: boolean) => {
    if (approving.current) return
    approving.current = true
    onNotice(null)
    try {
      onNotice(approvedNotice(await approve.mutateAsync({ grantId: row.id, trustBooth, vendorEventId: row.vendor_event })))
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not approve the phone.') })
    } finally {
      approving.current = false
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Waiting for approval</CardTitle>
        <CardDescription>
          Phones asking now. Check the name matches the phone in front of you before approving. This list updates by itself.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {waiting.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No phone is waiting.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {waiting.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0">
                <div className="flex items-start gap-3 min-w-0">
                  <Smartphone className="w-5 h-5 mt-0.5 shrink-0 text-neutral-400" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 break-words">{row.name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Asked {relativeTime(row.requested_at)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={approve.isPending} onClick={() => void run(row, false)}>Approve</Button>
                  <Button size="sm" variant="outline" disabled={approve.isPending} onClick={() => void run(row, true)}>
                    Approve and trust this booth
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <ApproveByCode eventId={eventId} onNotice={onNotice} />
      </CardContent>
    </Card>
  )
}

/** The fallback: the 6-digit code a phone shows, typed in. */
function ApproveByCode({ eventId, onNotice }: { eventId: number; onNotice: (notice: Notice | null) => void }) {
  const approve = useApprovePosGrantByCode(eventId)
  const [code, setCode] = useState('')
  const [trustBooth, setTrustBooth] = useState(false)
  // A second Enter before the button disables would approve, then report
  // pairing_code_not_found over the success.
  const approving = useRef(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (approving.current) return
    const digits = code.replace(/\D/g, '')
    if (digits.length !== 6) {
      onNotice({ kind: 'error', text: 'Enter the 6-digit code shown on the phone.' })
      return
    }
    onNotice(null)
    approving.current = true
    try {
      onNotice(approvedNotice(await approve.mutateAsync({ code: digits, trustBooth })))
      setCode('')
      setTrustBooth(false)
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not approve the phone.') })
    } finally {
      approving.current = false
    }
  }

  return (
    <form onSubmit={submit} className="pt-4 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
      <p className="text-sm text-neutral-700 dark:text-neutral-300">Or enter the code the phone shows:</p>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="w-40 font-mono tracking-widest"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          placeholder="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
          <input type="checkbox" checked={trustBooth} onChange={(e) => setTrustBooth(e.target.checked)} />
          Also trust this booth for the event
        </label>
        <Button type="submit" size="sm" variant="outline" disabled={approve.isPending}>
          {approve.isPending ? 'Approving…' : 'Approve phone'}
        </Button>
      </div>
    </form>
  )
}

function ApprovedPhones({ eventId, grants, devices, onNotice }: {
  eventId: number
  grants: PosGrant[]
  devices: EventTerminalDevice[]
  onNotice: (notice: Notice | null) => void
}) {
  const approved = grants.filter((g) => g.status === 'active')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Approved phones</CardTitle>
        <CardDescription>
          Revoking a phone stops its next card session here. A lost phone: revoke it, then ask EventPay to rotate the event's card key.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {approved.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No phone is approved yet.</p>
        ) : (
          <div className={SECTION}>
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className={TH}>Phone</th>
                  <th className={TH}>How approved</th>
                  <th className={TH}>Approved</th>
                  <th className={TH}>Last session</th>
                  <th className={TH}>Expires</th>
                  <th className={TH}>Tap to Pay</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {approved.map((row) => {
                  const tapToPay = devices.find((d) => d.device_model === 'IOS_TAP_TO_PAY' && d.pos_client === row.phone)
                  return (
                    <tr key={row.id} className={ROW}>
                      <td className={cn(TD, 'whitespace-normal min-w-[14rem] font-medium text-neutral-900 dark:text-neutral-100')}>{row.name}</td>
                      <td className={TD}>
                        {APPROVAL_LABEL[row.approval] ?? row.approval}
                        {row.approved_by && <div className="text-xs text-neutral-500 dark:text-neutral-400">by {row.approved_by}</div>}
                      </td>
                      <td className={TD}>{fmtDateTime(row.approved_at)}</td>
                      <td className={TD}>{row.last_session_at ? relativeTime(row.last_session_at) : 'Not yet'}</td>
                      <td className={TD}>{fmtDateTime(row.expires_at)}</td>
                      <td className={TD}>{tapToPay ? <DeviceStatus device={tapToPay} /> : EMPTY}</td>
                      <td className={cn(TD, 'text-right')}><RevokeButton eventId={eventId} row={row} onNotice={onNotice} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RevokeButton({ eventId, row, onNotice }: { eventId: number; row: PosGrant; onNotice: (notice: Notice | null) => void }) {
  const revoke = useRevokePosGrant(eventId)
  const [open, setOpen] = useState(false)

  if (!open) {
    return <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Revoke</Button>
  }
  return (
    <div className="flex justify-end gap-2">
      <Button
        size="sm"
        variant="destructive"
        disabled={revoke.isPending}
        onClick={async () => {
          onNotice(null)
          try {
            await revoke.mutateAsync(row.id)
            onNotice({ kind: 'success', text: `${row.name} can no longer take cards at this event.` })
          } catch (err) {
            onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not revoke the phone.') })
            setOpen(false)
          }
        }}
      >
        {revoke.isPending ? 'Revoking…' : 'Confirm revoke'}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  )
}

function TrustedBooths({ eventId, trusted, onNotice }: {
  eventId: number
  trusted: TrustedPosBooth[]
  onNotice: (notice: Notice | null) => void
}) {
  const setTrusted = useSetPosBoothTrusted(eventId)

  const untrust = async (booth: TrustedPosBooth) => {
    onNotice(null)
    try {
      await setTrusted.mutateAsync({ vendorEventId: booth.vendor_event, trusted: false })
      onNotice({ kind: 'success', text: `New phones from ${booth.name} need approval again. Phones already approved stay approved.` })
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not change the booth.') })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Trusted booths</CardTitle>
        <CardDescription>Any phone signed in as a trusted booth takes cards at this event without approval.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {trusted.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No booth is trusted.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {trusted.map((booth) => (
              <li key={booth.vendor_event} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0">
                <div className="flex items-start gap-3 min-w-0">
                  <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-green-600 dark:text-green-400" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{booth.name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Trusted {fmtDateTime(booth.created)}{booth.trusted_by ? ` by ${booth.trusted_by}` : ''}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" disabled={setTrusted.isPending} onClick={() => void untrust(booth)}>
                  Stop trusting
                </Button>
              </li>
            ))}
          </ul>
        )}
        <TrustBooth key={eventId} eventId={eventId} trusted={trusted} onNotice={onNotice} />
      </CardContent>
    </Card>
  )
}

/** Trust a booth before any of its phones asks. */
function TrustBooth({ eventId, trusted, onNotice }: {
  eventId: number
  trusted: TrustedPosBooth[]
  onNotice: (notice: Notice | null) => void
}) {
  const setTrusted = useSetPosBoothTrusted(eventId)
  const [search, setSearch] = useState('')
  const query = useDebouncedValue(search.trim(), 300)
  // Only once something is typed; and the vendor list needs vendor access,
  // which a login holding only can_add_terminal may not have. One refusal
  // answers for later searches until the box is cleared (access may have
  // been granted since). Searches are not cached, so a term typed again
  // asks again rather than showing its old refusal.
  const [noAccess, setNoAccess] = useState(false)
  const { data, isFetching, error } = useVendorEvents(
    eventId, { search: query, aggregates: false, limit: 8 },
    { enabled: !!query && !noAccess, retryForbidden: false, gcTime: 0 },
  )
  const forbidden = isForbidden(error)
  // Keyed on the debounced query: the old term's refusal stays on the query
  // until the cleared box has settled.
  if (query && forbidden && !noAccess) setNoAccess(true)
  if (!query && noAccess) setNoAccess(false)
  const trustedIds = new Set(trusted.map((t) => t.vendor_event))
  const booths = (data?.results ?? []).filter((ve) => !trustedIds.has(ve.id))

  // One at a time, even on a fast double click (disabled lands a render later).
  const trusting = useRef(false)
  const trust = async (vendorEventId: number) => {
    if (trusting.current) return
    trusting.current = true
    onNotice(null)
    try {
      await setTrusted.mutateAsync({ vendorEventId, trusted: true })
      onNotice({ kind: 'success', text: 'Phones signed in as that booth now take cards without approval.' })
      setSearch('')
    } catch (err) {
      onNotice({ kind: 'error', text: apiErrorDetail(err, 'Could not trust the booth.') })
    } finally {
      trusting.current = false
    }
  }

  return (
    <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
      <p className="text-sm text-neutral-700 dark:text-neutral-300">Trust a booth before its phones ask:</p>
      <Input className="max-w-sm" placeholder="Search vendors or booths" value={search} onChange={(e) => setSearch(e.target.value)} />
      {query && (noAccess || forbidden) ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Searching booths needs vendor access at this event. You can still trust a booth when approving one of its phones above.
        </p>
      ) : query && (
        booths.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {isFetching ? 'Searching…' : error ? 'Could not search the booths. Try again.' : 'No untrusted booth matches.'}
          </p>
        ) : (
          <ul className="max-w-sm divide-y divide-neutral-100 dark:divide-neutral-800">
            {booths.map((ve) => {
              const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ve.name
              return (
                <li key={ve.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-sm text-neutral-800 dark:text-neutral-200 truncate">
                    {vendorName}{ve.booth ? ` (Booth ${ve.booth})` : ''}
                  </span>
                  <Button size="sm" variant="outline" disabled={setTrusted.isPending} onClick={() => void trust(ve.id)}>Trust</Button>
                </li>
              )
            })}
          </ul>
        )
      )}
    </div>
  )
}

function DeviceStatus({ device }: { device: EventTerminalDevice }) {
  const label = device.status.replace(/_/g, ' ')
  return <span className={cn(PILL, 'capitalize', STATUS_CHIP[device.status] ?? NEUTRAL_CHIP)}>{label}</span>
}

function CardReaders({ grants, devices, error }: { grants: PosGrant[]; devices: EventTerminalDevice[]; error: unknown }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-neutral-400" aria-hidden />
          <CardTitle className="text-lg">Card readers</CardTitle>
        </div>
        <CardDescription>
          The D135 and Tap to Pay readers on this event's merchant. An iPhone's Tap to Pay reader is set up when the phone is approved;
          revoking the phone leaves its reader. Turning a reader off is done by EventPay.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {isForbidden(error)
              ? "This login cannot list the event's card readers: that needs card terminal access."
              : apiErrorDetail(error, 'Could not load the card readers.')}
          </p>
        ) : devices.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No card reader is set up for this event.</p>
        ) : (
          <div className={SECTION}>
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className={TH}>Reader</th>
                  <th className={TH}>Type</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Phone</th>
                  <th className={TH}>Last connection</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => {
                  const grant = device.device_model === 'IOS_TAP_TO_PAY' ? grantForPhone(grants, device.pos_client) : undefined
                  return (
                    <tr key={device.id} className={ROW}>
                      <td className={TD}>
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">{device.name}</span>
                        {device.description && <div className="text-xs text-neutral-500 dark:text-neutral-400">{device.description}</div>}
                      </td>
                      <td className={TD}>{MODEL_LABEL[device.device_model] ?? device.device_model}</td>
                      <td className={TD}><DeviceStatus device={device} /></td>
                      <td className={cn(TD, 'whitespace-normal')}>
                        {grant ? (
                          <>
                            {grant.name}
                            {grant.status !== 'active' && <div className="text-xs text-neutral-500 dark:text-neutral-400">Not approved ({grant.status.replace(/_/g, ' ')})</div>}
                          </>
                        ) : device.pos_client ? `Phone #${device.pos_client}` : EMPTY}
                      </td>
                      <td className={TD}>{fmtDateTime(device.last_connection)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
